import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/context/AdminAuthContext";
import { imgSrc } from "@/lib/img";
import { Card, PageTitle, Btn, Empty } from "@/admin/ui";
import { ProductForm } from "@/admin/ProductForm";

const STOCK = { in_stock: ["In stock", "bg-[#E8F0EA] text-forest"], sold_out: ["Sold out", "bg-red-50 text-red-700"], coming_soon: ["Coming soon", "bg-[#FBF6E8] text-gold"] };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => adminApi.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (editing !== null) {
    return (
      <div>
        <PageTitle title={editing?.id ? `Edit: ${editing.name}` : "New product"} sub="Prices and discounts update on the website instantly." />
        <Card className="p-6 sm:p-8">
          <ProductForm product={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-products-page">
      <PageTitle title="Products & Prices" sub="Edit prices, MRP, discounts, availability and images." action={<Btn data-testid="add-product-button" onClick={() => setEditing({})}><Plus size={14} /> Add product</Btn>} />
      <Card className="overflow-hidden">
        {products.length === 0 && <Empty text="No products yet." />}
        <ul className="divide-y divide-forest/10">
          {products.map((p) => {
            const [stockLabel, stockCls] = STOCK[p.stock] || STOCK.in_stock;
            const hasDiscount = p.discount_type && p.discount_type !== "none" && p.discount_value > 0;
            const final = p.discount_type === "percent" ? p.price - (p.price * p.discount_value) / 100 : p.discount_type === "flat" ? p.price - p.discount_value : p.price;
            const outVariants = (p.variants || []).filter((v) => v.stock !== "in_stock").length;
            return (
              <li key={p.id} data-testid={`admin-product-row-${p.id}`} className={`flex items-center gap-4 px-4 sm:px-6 py-4 ${p.active === false ? "opacity-50" : ""}`}>
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-creamdark shrink-0">
                  {p.image && <img src={imgSrc(p.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-forest text-lg truncate">{p.name}</p>
                    {p.badge && <span className="text-[9px] font-bold uppercase tracking-[0.15em] bg-forest text-cream px-2 py-0.5 rounded-full">{p.badge}</span>}
                    <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${stockCls}`}>{stockLabel}</span>
                    {p.active === false && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] text-mutedgreen"><EyeOff size={10} /> Hidden</span>}
                  </div>
                  <p className="text-xs text-mutedgreen truncate">{p.collection} · {p.variants?.length ? `${p.variants.length} pack sizes${outVariants ? ` (${outVariants} out of stock)` : ""}` : p.unit}</p>
                </div>
                <div className="text-right shrink-0">
                  <p data-testid={`admin-product-price-${p.id}`} className="font-semibold text-forest">₹{Math.max(final, 0).toFixed(0)}</p>
                  {hasDiscount && <p className="text-[11px] text-mutedgreen"><s>₹{p.price}</s> <span className="text-gold font-bold">{p.discount_type === "percent" ? `${p.discount_value}% off` : `₹${p.discount_value} off`}</span></p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} aria-label="Edit" className="p-2 rounded-full text-forest hover:bg-forest/5"><Pencil size={15} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} aria-label="Delete" className="p-2 rounded-full text-mutedgreen hover:text-red-700 hover:bg-red-50"><Trash2 size={15} /></button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
